import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';
import type { UserProfile, UserRole } from './types';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  userRole: UserRole;
  isLoading: boolean;
  isAdmin: () => boolean;
  isStaff: () => boolean;
  requiresPasswordChange: boolean;
  signOut: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  refreshSession: () => Promise<void>;
  hasAccess: (requiredRole: UserRole | UserRole[]) => boolean;
  updateProfile: (updates: Partial<UserProfile>) => Promise<UserProfile | null>;
  updateModuleProgress: (moduleId: string, progress: number) => Promise<boolean>;
  enrollInCourse: (courseId: string) => Promise<boolean>;
  userModuleProgress: Array<{module_id: string, progress: number}>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface EnhancedSupabaseAuthProviderProps {
  children: ReactNode;
}

export const EnhancedSupabaseAuthProvider: React.FC<EnhancedSupabaseAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [userRole, setUserRole] = useState<UserRole>('user');
  const [isLoading, setIsLoading] = useState(true);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);

  // Create functions instead of boolean properties
  const isAdmin = () => userRole === 'admin';
  const isStaff = () => userRole === 'staff' || userRole === 'admin';

  const fetchUserProfile = async (userId: string) => {
    try {
      const { data: profileData, error: profileError } = await supabase
        .rpc('get_or_create_profile', { user_id_param: userId });

      if (profileError) {
        logger.error('Error fetching user profile:', profileError);
        return null;
      }

      if (profileData && profileData.length > 0) {
        const profile = profileData[0];
        setProfile(profile);
        setRequiresPasswordChange(profile.requires_password_change || false);
        return profile;
      }

      return null;
    } catch (error) {
      logger.error('Error in fetchUserProfile:', error);
      return null;
    }
  };

  const fetchUserRole = async (userId: string): Promise<UserRole> => {
    try {
      const { data, error } = await supabase
        .rpc('get_user_role_safe', { user_id_param: userId });

      if (error) {
        logger.error('Error fetching user role:', error);
        return 'user';
      }

      return (data as UserRole) || 'user';
    } catch (error) {
      logger.error('Error in fetchUserRole:', error);
      return 'user';
    }
  };

  const refreshUserData = async () => {
    if (!user) return;

    try {
      const [profileData, roleData] = await Promise.all([
        fetchUserProfile(user.id),
        fetchUserRole(user.id)
      ]);

      if (profileData) {
        setProfile(profileData);
        setRequiresPasswordChange(profileData.requires_password_change || false);
      }
      setUserRole(roleData);
    } catch (error) {
      logger.error('Error refreshing user data:', error);
    }
  };

  const refreshSession = async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        logger.error('Error refreshing session:', error);
      } else {
        setSession(data.session);
        setUser(data.session?.user || null);
      }
    } catch (error) {
      logger.error('Error in refreshSession:', error);
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      
      // Clear local state first
      setUser(null);
      setSession(null);
      setProfile(null);
      setUserRole('user');
      setRequiresPasswordChange(false);

      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) {
        logger.error('Error signing out:', error);
      }
    } catch (error) {
      logger.error('Error in signOut:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const hasAccess = (requiredRole: UserRole | UserRole[]): boolean => {
    if (!userRole) return false;
    
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    
    for (const role of roles) {
      if (role === 'user') return true;
      if (role === 'staff' && (userRole === 'staff' || userRole === 'admin')) return true;
      if (role === 'admin' && userRole === 'admin') return true;
    }
    
    return false;
  };

  // Add backward compatibility methods
  const updateProfile = async (updates: Partial<UserProfile>): Promise<UserProfile | null> => {
    if (!user) return null;
    // Placeholder implementation - extend as needed
    return profile;
  };

  const updateModuleProgress = async (moduleId: string, progress: number): Promise<boolean> => {
    // Placeholder implementation - extend as needed
    return true;
  };

  const enrollInCourse = async (courseId: string): Promise<boolean> => {
    // Placeholder implementation - extend as needed
    return true;
  };

  const userModuleProgress = Object.entries(profile?.progress || {}).map(([module_id, progress]) => ({
    module_id,
    progress
  }));

  useEffect(() => {
    let mounted = true;

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        logger.info(`Auth state changed: ${event}`);
        
        // Update session and user state synchronously
        setSession(session);
        setUser(session?.user || null);

        // Handle different auth events
        if (event === 'SIGNED_IN' && session?.user) {
          // Defer profile and role fetching to avoid blocking auth state updates
          setTimeout(async () => {
            if (mounted) {
              await Promise.all([
                fetchUserProfile(session.user.id),
                fetchUserRole(session.user.id).then(role => {
                  if (mounted) setUserRole(role);
                })
              ]);
              
              if (mounted) setIsLoading(false);
            }
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          setProfile(null);
          setUserRole('user');
          setRequiresPasswordChange(false);
          setIsLoading(false);
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          // Session refreshed, update any dependent data if needed
          setTimeout(() => {
            if (mounted) refreshUserData();
          }, 0);
        }
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (!mounted) return;
      
      if (error) {
        logger.error('Error getting initial session:', error);
        setIsLoading(false);
        return;
      }

      setSession(session);
      setUser(session?.user || null);

      if (session?.user) {
        // Fetch initial profile and role data
        Promise.all([
          fetchUserProfile(session.user.id),
          fetchUserRole(session.user.id)
        ]).then(([profileData, roleData]) => {
          if (mounted) {
            if (profileData) {
              setProfile(profileData);
              setRequiresPasswordChange(profileData.requires_password_change || false);
            }
            setUserRole(roleData);
            setIsLoading(false);
          }
        }).catch(error => {
          logger.error('Error fetching initial user data:', error);
          if (mounted) setIsLoading(false);
        });
      } else {
        setIsLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const value: AuthContextType = {
    user,
    session,
    profile,
    userRole,
    isLoading,
    isAdmin,
    isStaff,
    requiresPasswordChange,
    signOut,
    refreshUserData,
    refreshSession,
    hasAccess,
    updateProfile,
    updateModuleProgress,
    enrollInCourse,
    userModuleProgress
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Safe auth hook that returns a stub when provider is not available
export const useEnhancedSupabaseAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    // Return a safe default stub instead of throwing
    console.warn('useEnhancedSupabaseAuth used outside provider, returning safe defaults');
    return {
      user: null,
      session: null,
      profile: null,
      userRole: 'user',
      isLoading: false,
      isAdmin: () => false,
      isStaff: () => false,
      requiresPasswordChange: false,
      signOut: async () => {},
      refreshUserData: async () => {},
      refreshSession: async () => {},
      hasAccess: () => false,
      updateProfile: async () => null,
      updateModuleProgress: async () => false,
      enrollInCourse: async () => false,
      userModuleProgress: []
    };
  }
  return context;
};